
import React, { useState } from 'react'
import { X, Calendar, User, Phone, DollarSign, Tag, FileText } from 'lucide-react'

interface Lead {
  _id: string
  nome: string
  contato: string
  status: 'prospect' | 'negociacao' | 'perdido' | 'fechado'
  origem: string
  valor: number
  responsavel: string
  observacoes?: string
  createdAt: string
  updatedAt: string
}

interface LeadDetailProps {
  lead: Lead | null
  isOpen: boolean
  onClose: () => void
  onUpdateStatus: (leadId: string, newStatus: Lead['status']) => Promise<void>
}

const LeadDetail: React.FC<LeadDetailProps> = ({
  lead,
  isOpen,
  onClose,
  onUpdateStatus
}) => {
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false)

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'prospect': return 'bg-blue-100 text-blue-800'
      case 'negociacao': return 'bg-yellow-100 text-yellow-800'
      case 'fechado': return 'bg-green-100 text-green-800'
      case 'perdido': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'prospect': return 'Prospect'
      case 'negociacao': return 'Em Negociação'
      case 'fechado': return 'Fechado'
      case 'perdido': return 'Perdido'
      default: return status
    }
  }

  const handleStatusChange = async (newStatus: Lead['status']) => {
    if (!lead) return
    
    setIsUpdatingStatus(true)
    try {
      await onUpdateStatus(lead._id, newStatus)
    } catch (error) {
      // Erro já tratado no hook
    } finally {
      setIsUpdatingStatus(false)
    }
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR')
  }

  if (!isOpen || !lead) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Detalhes do Lead</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X size={24} />
          </button>
        </div>

        <div className="space-y-6">
          {/* Nome e Status */}
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-semibold text-gray-800">{lead.nome}</h3>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Status:</span>
              <select
                value={lead.status}
                onChange={(e) => handleStatusChange(e.target.value as Lead['status'])}
                disabled={isUpdatingStatus}
                className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(lead.status)} border-0 focus:ring-2 focus:ring-blue-500`}
              >
                <option value="prospect">Prospect</option>
                <option value="negociacao">Em Negociação</option>
                <option value="fechado">Fechado</option>
                <option value="perdido">Perdido</option>
              </select>
            </div>
          </div>

          {/* Informações principais */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <Phone className="text-blue-600" size={20} />
              <div>
                <p className="text-sm text-gray-600">Contato</p>
                <p className="font-medium">{lead.contato}</p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <DollarSign className="text-green-600" size={20} />
              <div>
                <p className="text-sm text-gray-600">Valor</p>
                <p className="font-medium">{formatCurrency(lead.valor)}</p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <Tag className="text-purple-600" size={20} />
              <div>
                <p className="text-sm text-gray-600">Origem</p>
                <p className="font-medium">{lead.origem}</p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <User className="text-orange-600" size={20} />
              <div>
                <p className="text-sm text-gray-600">Responsável</p>
                <p className="font-medium">{lead.responsavel}</p>
              </div>
            </div>
          </div>

          {/* Observações */}
          {lead.observacoes && (
            <div className="p-4 bg-blue-50 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="text-blue-600" size={20} />
                <h4 className="font-medium text-gray-800">Observações</h4>
              </div>
              <p className="text-gray-700">{lead.observacoes}</p>
            </div>
          )}

          {/* Datas */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t">
            <div className="flex items-center gap-3">
              <Calendar className="text-gray-500" size={20} />
              <div>
                <p className="text-sm text-gray-600">Criado em</p>
                <p className="font-medium">{formatDate(lead.createdAt)}</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Calendar className="text-gray-500" size={20} />
              <div>
                <p className="text-sm text-gray-600">Última atualização</p>
                <p className="font-medium">{formatDate(lead.updatedAt)}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end pt-6 border-t mt-6">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  )
}

export default LeadDetail
